import React, { useContext, useState } from "react";
import styled from "styled-components";
import { AuthUserContext } from "../../context/AuthUserContext";
import CheckoutAddresses from "../popup/CheckoutAddresses";
import { useTranslation } from "react-i18next";
import AddEditAddressCheckout from "../account/AddEditAddressCheckout";

const Container = styled.div`
  margin: 0 auto;
  width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const InnerWrapper = styled.div`
  border-radius: 10px;

  width: 100%;
`;

const Row = styled.div`
  display: flex;
  gap: 0px;
`;

const GridRow = styled.div`
  display: flex;
  gap: 20px;
`;

const GridRowReversed = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: row-reverse;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  display: block;
`;

const Input = styled.input`
  padding: 10px;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 8px;
  margin-top: 5px;
  margin-bottom: 15px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const Button = styled.button`
  margin: 14px 0;
  width: 94%;

  padding: 10px 20px;
  background-color: ${({ disabled }) =>
    disabled ? "#d3d3d3" : "var(--primary-200)"};
  color: ${({ disabled }) => (disabled ? "#9e9e9e" : "var(--bg-200)")};
  font-size: 14px;
  font-weight: bold;
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? "#d3d3d3" : "var(--primary-100)"};
  }
  text-align: center;
  @media (max-width: 767px) {
    margin: 0 auto;
  }

  @media (min-width: 767px) {
    width: 30%;
  }
`;

const CardsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 14px;
  margin: 24px 0;
  @media (min-width: 767px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const AddressCard = styled.div`
  position: relative;
  background-color: var(--bg-200);
  width: 100%;
  padding: 0;
  margin: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) 0;
  box-shadow: var(--shadow-medium);
  border-radius: 4px;
  cursor: pointer;
  border-bottom: 1px solid var(--bg-300);
  border-right: 1px solid var(--bg-300);

  border: ${(props) => (props.selected ? "2px solid black" : "1px solid #ccc")};
  background-color: ${(props) => (props.selected ? "#f0f0f0" : "white")};
  transition: background-color 0.3s, border 0.3s;

  p {
    margin: 5px 0;
    font-size: 14px;
  }
`;
const AddressCardWrapper = styled.div`
  width: 90%;
`;
const Step1Exists = ({ onNext }) => {
  const { t } = useTranslation();
  const { userProfile } = useContext(AuthUserContext);

  const allShippingAddresses =
    userProfile?.addresses?.filter((addr) => addr.type === "shipping") || [];
  const allBillingAddresses =
    userProfile?.addresses?.filter((addr) => addr.type === "billing") || [];

  const [sameAsBilling, setSameAsBilling] = useState(true);

  const [selectedShippingAddress, setSelectedShippingAddress] = useState(
    allShippingAddresses[0] || null
  );
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(
    allBillingAddresses[0] || null
  );

  const [editShipping, setEditShipping] = useState(null);
  const [editBilling, setEditBilling] = useState(null);

  const handleContinue = () => {
    const shippingAddress = editShipping || selectedShippingAddress;
    const billingAddress = sameAsBilling
      ? editShipping || selectedShippingAddress
      : editBilling || selectedBillingAddress;

    if (!shippingAddress || !billingAddress) {
      alert("Please select or complete addresses.");
      return;
    }
    onNext({
      shipping_address: shippingAddress,
      billing_address: billingAddress,
    });
  };

  return (
    <Container>
      <Wrapper>
        {/* SHIPPING */}
        <InnerWrapper>
          <h3>{t("CHECKOUT.SELECT_SHIPPING")}</h3>

          {allShippingAddresses.length > 0 && !editShipping ? (
            <>
              <CardsWrapper>
                {allShippingAddresses.map((address) => (
                  <AddressCard
                    key={address.id}
                    selected={selectedShippingAddress?.id === address.id}
                    onClick={() => {
                      setSelectedShippingAddress(address);
                      setEditShipping(null);
                    }}
                  >
                    <AddressCardWrapper>
                      <p>{`${address.street_name} ${address.number}`}</p>
                      <p>{`${address.city}, ${address.postal_code}`}</p>
                      <p>{`${address.country}`}</p>
                    </AddressCardWrapper>
                  </AddressCard>
                ))}
              </CardsWrapper>
              <Button
                type="button"
                onClick={() => {
                  setEditShipping({
                    ...selectedShippingAddress,
                  });
                }}
              >
                {t("CHECKOUT.EDIT_SHIPPING")}
              </Button>
            </>
          ) : (
            <AddEditAddressCheckout
              editAddress={editShipping}
              setEditAddress={setEditShipping}
              type={"shipping"}
              sameAsBilling={sameAsBilling}
            />
          )}
        </InnerWrapper>

        {/* CHECKBOX "SAME AS BILLING" */}
        <InnerWrapper>
          <Row>
            <input
              type="checkbox"
              checked={sameAsBilling}
              onChange={(e) => setSameAsBilling(e.target.checked)}
            />
            <label style={{ marginLeft: "10px" }}>
              {t("CHECKOUT.USE_SAME_BILLING")}
            </label>
          </Row>
        </InnerWrapper>

        {/* BILLING */}
        {!sameAsBilling && (
          <InnerWrapper>
            <h3>{t("CHECKOUT.SELECT_BILLING")}</h3>

            {allBillingAddresses.length > 0 && !editBilling ? (
              <>
                <CardsWrapper>
                  {allBillingAddresses.map((address) => (
                    <AddressCard
                      key={address.id}
                      selected={selectedBillingAddress?.id === address.id}
                      onClick={() => {
                        setSelectedBillingAddress(address);
                        setEditBilling(null);
                      }}
                    >
                      <p>{`${address.street_name} ${address.number}`}</p>
                      <p>{`${address.city}, ${address.postal_code}`}</p>
                      <p>{`${address.country}`}</p>
                    </AddressCard>
                  ))}
                </CardsWrapper>
                <Button
                  type="button"
                  onClick={() => {
                    setEditBilling({
                      ...selectedBillingAddress,
                    });
                  }}
                >
                  {t("CHECKOUT.EDIT_BILLING")}
                </Button>
              </>
            ) : (
              <AddEditAddressCheckout
                editAddress={editBilling}
                setEditAddress={setEditBilling}
                type={"billing"}
              />
            )}
          </InnerWrapper>
        )}

        {/* CONTINUE BUTTON */}
        <InnerWrapper style={{ margin: "20px 0" }}>
          <Button onClick={handleContinue}>{t("BUTTONS.CONTINUE")}</Button>
        </InnerWrapper>
      </Wrapper>
    </Container>
  );
};

export default Step1Exists;
